import React, { useEffect, useState } from 'react';
import {
    Button,
    Typography,
    Box,
    LinearProgress,
    CircularProgress,
} from '@mui/material';
import { AccessTime, Warning } from '@mui/icons-material';
import { DtDialog } from './ui/dialog';
import { DtLoader } from './ui/loader';

interface SessionExpirationModalProps {
    open: boolean;
    secondsRemaining: number;
    onRefresh: () => void;
    onExpire: () => void;
    isRefreshing: boolean;
}

const SessionExpirationModal: React.FC<SessionExpirationModalProps> = ({
    open,
    secondsRemaining,
    onRefresh,
    onExpire,
    isRefreshing,
}) => {
    const [countdown, setCountdown] = useState(secondsRemaining);

    // Reset countdown when modal opens or secondsRemaining changes
    useEffect(() => {
        if (open) {
            setCountdown(secondsRemaining);
        }
    }, [secondsRemaining, open]);

    useEffect(() => {
        if (!open || isRefreshing) return;

        const timer = setInterval(() => {
            setCountdown((prev) => {
                const newValue = prev - 1;
                if (newValue <= 0) {
                    clearInterval(timer);
                    onExpire();
                    return 0;
                }
                return newValue;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [open, onExpire, isRefreshing]);

    const progressValue = (countdown / secondsRemaining) * 100;
    const isUrgent = countdown <= 10;

    const dialogTitle = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning
                sx={{
                    color: isUrgent ? 'error.main' : 'warning.main',
                    fontSize: 24,
                }}
            />
            <Typography variant="h6" component="span">
                Session Expiring Soon
            </Typography>
        </Box>
    );

    const dialogActions = (
        <Box sx={{ display: 'flex', gap: 1, width: '100%', px: 1 }}>
            <Button
                onClick={onExpire}
                variant="outlined"
                disabled={isRefreshing}
                size="small"
                sx={{ flex: 1 }}
            >
                Log Out Now
            </Button>
            <Button
                onClick={onRefresh}
                variant="contained"
                color="primary"
                disabled={isRefreshing}
                size="small"
                sx={{ flex: 1 }}
            >
                Stay Logged In
            </Button>
        </Box>
    );

    return (
        // Note: onClose is omitted because isClosable={false} prevents all dialog-initiated close actions
        // (backdrop click, ESC key, X button). The dialog can only be closed via the action buttons.
        <DtDialog
            open={open}
            onClose={() => {}} // Required prop but never called when isClosable={false}
            title={dialogTitle}
            actions={dialogActions}
            maxWidth="xs"
            fullWidth
            isClosable={false}
            sx={{
                dialog: { '& .MuiPaper-root': { borderRadius: 2 } },
                content: { p: 2 },
                actions: { px: 1, py: 1.5 },
            }}
        >
            <Box sx={{ textAlign: 'center' }}>
                {isRefreshing ? (
                    <Box sx={{ py: 3 }}>
                        <DtLoader message="Refreshing your session..." />
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'inline-flex',
                                mb: 2,
                            }}
                        >
                            <CircularProgress
                                variant="determinate"
                                value={progressValue}
                                size={120}
                                thickness={3}
                                sx={{
                                    color: isUrgent ? 'error.main' : 'warning.main',
                                }}
                            />
                            <Box
                                sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: 'absolute',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    p: 2,
                                }}
                            >
                                <AccessTime
                                    sx={{
                                        fontSize: 24,
                                        color: isUrgent ? 'error.main' : 'warning.main',
                                        mb: 0.5,
                                    }}
                                />
                                <Typography
                                    variant="h3"
                                    component="div"
                                    sx={{
                                        fontWeight: 700,
                                        color: isUrgent ? 'error.main' : 'warning.main',
                                        fontSize: '1.75rem',
                                    }}
                                >
                                    {countdown}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.65rem' }}
                                >
                                    seconds
                                </Typography>
                            </Box>
                        </Box>

                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Your session is about to expire
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            Click "Stay Logged In" to continue working, or you'll be redirected to
                            Encompass in {countdown} seconds.
                        </Typography>

                        <LinearProgress
                            variant="determinate"
                            value={progressValue}
                            sx={{
                                mt: 2,
                                height: 6,
                                borderRadius: 1,
                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: isUrgent ? 'error.main' : 'warning.main',
                                    borderRadius: 1,
                                },
                            }}
                        />
                    </>
                )}
            </Box>
        </DtDialog>
    );
};

export default SessionExpirationModal;
